import {
    Service,
    ServiceSellPrice,
    ServiceSellCost,
    PriceGeneratorConfig,
    SeedPriceHistoryItem,
    DEFAULT_CURRENCY
} from "@turizi/shared";
import { useTranslation } from "react-i18next";
import {
    Button,
    Checkbox,
    NumberInput,
    Select,
    Badge,
    SegmentedControl,
    Switch
} from "@mantine/core";
import {
    IconSparkles,
    IconBuildingStore,
    IconCheck,
    IconTrendingUp,
    IconTrash,
} from "@tabler/icons-react";
import { useState, useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateService, useCreateServiceSellPrice, useCreateServiceSellCost, useUpdateTenantBusiness, queryKeys } from "@/hooks/api-hooks";
import { notify } from "@/lib/notify";
import { formatDay, formatPrice } from "@/lib/format-utils";
import CurrencySelector from "../currency-selector";
import { MoneyNumberInput } from "../snippets";
import { TagIcon } from "@/lib/tag-utils";
import DatePickerCustom from "../custom-date-picker";

export function applyRounding(val: number, step: number, method: 'round' | 'ceil' | 'floor' = 'round'): number {
    if (!step || step <= 0 || val === 0) return val;
    let res = val;
    if (step < 1) {
        const factor = Math.round(1 / step);
        if (method === 'ceil') res = Math.ceil(val * factor) / factor;
        else if (method === 'floor') res = Math.floor(val * factor) / factor;
        else res = Math.round(val * factor) / factor;
    } else {
        if (method === 'ceil') res = Math.ceil(val / step) * step;
        else if (method === 'floor') res = Math.floor(val / step) * step;
        else res = Math.round(val / step) * step;
    }
    if (val > 0 && res === 0) {
        return method === 'floor' ? 0 : step;
    }
    return res;
}

export function formatModifier(val: number | null | undefined): string {
    if (val === null || val === undefined || isNaN(val)) return '1.0000000000';
    return Number(val).toFixed(10);
}

export function modifierToPercent(mod: number | string | null | undefined): string {
    if (mod === null || mod === undefined || mod === '' || isNaN(Number(mod))) return '0.00%';
    const pct = (Number(mod) - 1) * 100;
    const sign = pct > 0 ? '+' : '';
    return `${sign}${pct.toFixed(2)}%`;
}

export function getEffectiveDiff(diff: number | string | null | undefined): number {
    if (diff === '' || diff === null || diff === undefined) return 1;
    const num = Number(diff);
    return isNaN(num) ? 1 : num;
}

export function getEffectiveTaxModifier(mod: number | string | null | undefined): number {
    if (mod === '' || mod === null || mod === undefined) return 1;
    const num = Number(mod);
    return isNaN(num) ? 1 : num;
}

interface CategoryDiffState {
    enabled: boolean;
    diff: number | string;
    currency: string;
}

export interface ServicePriceGeneratorTabProps {
    service: Service;
}

export default function ServicePriceGeneratorTab({ service }: ServicePriceGeneratorTabProps) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const existingConfig: PriceGeneratorConfig | null = service.price_generator_config || null;

    const initialSeedPrices: SeedPriceHistoryItem[] = useMemo(() => {
        if (existingConfig?.seed_prices && Array.isArray(existingConfig.seed_prices)) {
            return existingConfig.seed_prices;
        }
        if (existingConfig?.seed_price) {
            return [{
                id: '1',
                amount: existingConfig.seed_price,
                currency: existingConfig.currency || DEFAULT_CURRENCY,
                start_day: existingConfig.start_day || null,
                end_day: existingConfig.end_day || null,
                created_at: existingConfig.updated_at || new Date().toISOString(),
            }];
        }
        return [];
    }, [existingConfig]);

    const [seedPricesHistory, setSeedPricesHistory] = useState<SeedPriceHistoryItem[]>(initialSeedPrices);

    const initialCurrency = useMemo(() => {
        return existingConfig?.currency || service.sell_prices?.[0]?.default_currency || DEFAULT_CURRENCY;
    }, [existingConfig, service.sell_prices]);

    const initialTaxModifier = useMemo((): number | string => {
        if (existingConfig?.tax_modifier != null) {
            return Number(existingConfig.tax_modifier) === 1 ? '' : Number(existingConfig.tax_modifier);
        }
        if (service.tax_percentage != null && Number(service.tax_percentage) !== 0) {
            return Number((1 + Number(service.tax_percentage) / 100).toFixed(10));
        }
        return '';
    }, [existingConfig, service.tax_percentage]);

    // Core state - starts empty/null so no magic number is prefilled
    const [seedPrice, setSeedPrice] = useState<number | null>(null);
    const [currency, setCurrency] = useState<string>(initialCurrency);
    const [taxModifier, setTaxModifier] = useState<number | string>(initialTaxModifier);

    // Rounding state (default: 1)
    const [roundTo, setRoundTo] = useState<number>(existingConfig?.round_to ?? 1);
    const [roundMethod, setRoundMethod] = useState<'round' | 'ceil' | 'floor'>(existingConfig?.round_method ?? 'round');
    const [costBase, setCostBase] = useState<'seed_price' | 'sell_prices'>(existingConfig?.cost_base ?? 'seed_price');

    // Date range
    const [startDay, setStartDay] = useState<string | null>(existingConfig?.start_day);
    const [endDay, setEndDay] = useState<string | null>(existingConfig?.end_day);

    const toInitialDiff = (val: number | null | undefined): number | string => {
        if (val === null || val === undefined || Number(val) === 1) return '';
        return Number(val);
    };

    // Category differentials - unchecked by default, empty (1x) by default, with per-category currency
    const [categories, setCategories] = useState<{
        default: CategoryDiffState;
        foreigner: CategoryDiffState;
        child: CategoryDiffState;
        senior: CategoryDiffState;
        infant: CategoryDiffState;
        provider: CategoryDiffState;
    }>(() => ({
        default: {
            enabled: true,
            diff: toInitialDiff(existingConfig?.diff_default),
            currency: existingConfig?.currency_default || initialCurrency,
        },
        foreigner: {
            enabled: existingConfig ? existingConfig.diff_foreigner !== null && existingConfig.diff_foreigner !== undefined : false,
            diff: toInitialDiff(existingConfig?.diff_foreigner),
            currency: existingConfig?.currency_foreigner || initialCurrency,
        },
        child: {
            enabled: existingConfig ? existingConfig.diff_child !== null && existingConfig.diff_child !== undefined : false,
            diff: toInitialDiff(existingConfig?.diff_child),
            currency: existingConfig?.currency_child || initialCurrency,
        },
        senior: {
            enabled: existingConfig ? existingConfig.diff_senior !== null && existingConfig.diff_senior !== undefined : false,
            diff: toInitialDiff(existingConfig?.diff_senior),
            currency: existingConfig?.currency_senior || initialCurrency,
        },
        infant: {
            enabled: existingConfig ? existingConfig.diff_infant !== null && existingConfig.diff_infant !== undefined : false,
            diff: toInitialDiff(existingConfig?.diff_infant),
            currency: existingConfig?.currency_infant || initialCurrency,
        },
        provider: {
            enabled: existingConfig ? existingConfig.diff_provider !== null && existingConfig.diff_provider !== undefined : false,
            diff: toInitialDiff(existingConfig?.diff_provider),
            currency: existingConfig?.currency_provider || initialCurrency,
        },
    }));

    // Provider cost multipliers state
    const [providerMultipliers, setProviderMultipliers] = useState<Record<string, number | string>>({});

    const [isApplyingPrices, setIsApplyingPrices] = useState(false);
    const [isApplyingCosts, setIsApplyingCosts] = useState(false);

    const updateServiceMutation = useUpdateService();
    const createPriceMutation = useCreateServiceSellPrice();
    const createCostMutation = useCreateServiceSellCost();
    const updateTenantBusinessMutation = useUpdateTenantBusiness();

    const updateCategoryDiff = useCallback((key: keyof typeof categories, diff: number | string) => {
        setCategories(prev => ({
            ...prev,
            [key]: { ...prev[key], diff }
        }));
    }, []);

    const updateCategoryCurrency = useCallback((key: keyof typeof categories, curr: string) => {
        setCategories(prev => ({
            ...prev,
            [key]: { ...prev[key], currency: curr }
        }));
    }, []);

    const toggleCategoryEnabled = useCallback((key: keyof typeof categories, enabled: boolean) => {
        setCategories(prev => ({
            ...prev,
            [key]: { ...prev[key], enabled }
        }));
    }, []);

    const handleTopCurrencyChange = (newCurrency: string) => {
        const oldCurrency = currency;
        setCurrency(newCurrency);
        setCategories(prev => {
            const next = { ...prev };
            (Object.keys(next) as Array<keyof typeof next>).forEach(k => {
                if (next[k].currency === oldCurrency) {
                    next[k] = { ...next[k], currency: newCurrency };
                }
            });
            return next;
        });
    };

    // Calculated sell prices
    const calculatedSellPrices = useMemo(() => {
        const basePrice = seedPrice ?? 0;
        const effectiveTax = getEffectiveTaxModifier(taxModifier);
        const calc = (diff: number | string) => {
            const raw = basePrice * effectiveTax * getEffectiveDiff(diff);
            const rounded = applyRounding(raw, roundTo, roundMethod);
            return { raw, rounded };
        };

        return {
            default: calc(categories.default.diff),
            foreigner: categories.foreigner.enabled ? calc(categories.foreigner.diff) : null,
            child: categories.child.enabled ? calc(categories.child.diff) : null,
            senior: categories.senior.enabled ? calc(categories.senior.diff) : null,
            infant: categories.infant.enabled ? calc(categories.infant.diff) : null,
            provider: categories.provider.enabled ? calc(categories.provider.diff) : null,
        };
    }, [seedPrice, taxModifier, categories, roundTo, roundMethod]);

    const providersList = useMemo(() => {
        if (service.providers && service.providers.length > 0) {
            return service.providers;
        }
        return [];
    }, [service.providers]);

    // Calculate costs per provider
    const calculatedCosts = useMemo(() => {
        const basePrice = seedPrice ?? 0;
        const result: Record<string, {
            default: { raw: number; rounded: number };
            foreigner: { raw: number; rounded: number } | null;
            child: { raw: number; rounded: number } | null;
            senior: { raw: number; rounded: number } | null;
            infant: { raw: number; rounded: number } | null;
        }> = {};

        const calculateForMultiplier = (multiplier: number) => {
            const calc = (cat: 'default' | 'foreigner' | 'child' | 'senior' | 'infant') => {
                const raw = costBase === 'sell_prices'
                    ? (calculatedSellPrices[cat]?.rounded ?? 0) * multiplier
                    : basePrice * multiplier * getEffectiveDiff(categories[cat].diff);
                const rounded = applyRounding(raw, roundTo, roundMethod);
                return { raw, rounded };
            };
            return {
                default: calc('default'),
                foreigner: categories.foreigner.enabled ? calc('foreigner') : null,
                child: categories.child.enabled ? calc('child') : null,
                senior: categories.senior.enabled ? calc('senior') : null,
                infant: categories.infant.enabled ? calc('infant') : null,
            };
        };

        if (providersList.length > 0) {
            providersList.forEach(p => {
                const key = p.id ? p.id.toString() : 'all';
                const rawMult = key in providerMultipliers
                    ? providerMultipliers[key]
                    : (p.tenant_business?.cost_multiplier != null ? Number(p.tenant_business.cost_multiplier) : '');
                const mult = rawMult === '' || rawMult == null ? 0 : (Number(rawMult) || 0);
                result[key] = calculateForMultiplier(mult);
            });
        } else {
            const rawMult = 'all' in providerMultipliers ? providerMultipliers['all'] : '';
            const mult = rawMult === '' || rawMult == null ? 0 : (Number(rawMult) || 0);
            result['all'] = calculateForMultiplier(mult);
        }

        return result;
    }, [providersList, providerMultipliers, seedPrice, categories, roundTo, roundMethod, costBase, calculatedSellPrices]);

    const currentConfig = useMemo((): PriceGeneratorConfig => ({
        seed_price: seedPrice,
        seed_prices: seedPricesHistory,
        currency,
        tax_modifier: Number(getEffectiveTaxModifier(taxModifier).toFixed(10)),
        diff_default: Number(getEffectiveDiff(categories.default.diff).toFixed(10)),
        diff_foreigner: categories.foreigner.enabled ? Number(getEffectiveDiff(categories.foreigner.diff).toFixed(10)) : null,
        diff_child: categories.child.enabled ? Number(getEffectiveDiff(categories.child.diff).toFixed(10)) : null,
        diff_senior: categories.senior.enabled ? Number(getEffectiveDiff(categories.senior.diff).toFixed(10)) : null,
        diff_infant: categories.infant.enabled ? Number(getEffectiveDiff(categories.infant.diff).toFixed(10)) : null,
        diff_provider: categories.provider.enabled ? Number(getEffectiveDiff(categories.provider.diff).toFixed(10)) : null,
        currency_default: categories.default.currency,
        currency_foreigner: categories.foreigner.enabled ? categories.foreigner.currency : undefined,
        currency_child: categories.child.enabled ? categories.child.currency : undefined,
        currency_senior: categories.senior.enabled ? categories.senior.currency : undefined,
        currency_infant: categories.infant.enabled ? categories.infant.currency : undefined,
        currency_provider: categories.provider.enabled ? categories.provider.currency : undefined,
        round_to: roundTo,
        round_method: roundMethod,
        cost_base: costBase,
        start_day: startDay,
        end_day: endDay,
        updated_at: new Date().toISOString(),
    }), [seedPrice, seedPricesHistory, currency, taxModifier, categories, roundTo, roundMethod, costBase, startDay, endDay]);

    const buildUpdatedHistory = useCallback((currentSeed: number | null): SeedPriceHistoryItem[] => {
        if (!currentSeed || currentSeed <= 0) return seedPricesHistory;
        const existingIdx = seedPricesHistory.findIndex(
            item => item.amount === currentSeed && item.currency === currency && (item.start_day || null) === (startDay || null) && (item.end_day || null) === (endDay || null)
        );
        let updated: SeedPriceHistoryItem[];
        if (existingIdx >= 0) {
            const item = seedPricesHistory[existingIdx];
            updated = [item, ...seedPricesHistory.filter((_, i) => i !== existingIdx)];
        } else {
            updated = [{
                id: Date.now().toString(),
                amount: currentSeed,
                currency,
                start_day: startDay || null,
                end_day: endDay || null,
                created_at: new Date().toISOString()
            }, ...seedPricesHistory];
        }
        return updated;
    }, [seedPricesHistory, currency, startDay, endDay]);

    const handleApplyPrices = async () => {
        if (!service.id) return;
        if (!seedPrice || seedPrice <= 0) {
            notify.error('error', 'seed_price');
            return;
        }
        setIsApplyingPrices(true);
        try {
            const nextHistory = buildUpdatedHistory(seedPrice);
            setSeedPricesHistory(nextHistory);

            const configToSave: PriceGeneratorConfig = {
                ...currentConfig,
                seed_price: seedPrice,
                seed_prices: nextHistory,
            };

            await updateServiceMutation.mutateAsync({
                id: service.id,
                price_generator_config: configToSave
            });

            const sellPricePayload: Partial<ServiceSellPrice> = {
                service_id: service.id,
                start_day: startDay,
                end_day: endDay,
                default_amount: calculatedSellPrices.default.rounded,
                default_currency: categories.default.currency,
                foreigner_amount: calculatedSellPrices.foreigner ? calculatedSellPrices.foreigner.rounded : null,
                foreigner_currency: calculatedSellPrices.foreigner ? categories.foreigner.currency : null,
                child_amount: calculatedSellPrices.child ? calculatedSellPrices.child.rounded : null,
                child_currency: calculatedSellPrices.child ? categories.child.currency : null,
                senior_amount: calculatedSellPrices.senior ? calculatedSellPrices.senior.rounded : null,
                senior_currency: calculatedSellPrices.senior ? categories.senior.currency : null,
                infant_amount: calculatedSellPrices.infant ? calculatedSellPrices.infant.rounded : null,
                infant_currency: calculatedSellPrices.infant ? categories.infant.currency : null,
                provider_amount: calculatedSellPrices.provider ? calculatedSellPrices.provider.rounded : null,
                provider_currency: calculatedSellPrices.provider ? categories.provider.currency : null,
            };
            await createPriceMutation.mutateAsync(sellPricePayload);

            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceSellPrices.all });

            notify.success('success', 'prices_applied_successfully');
        } catch (err: any) {
            notify.error('error', err?.message || 'operation.error');
        } finally {
            setIsApplyingPrices(false);
        }
    };

    const handleApplyCosts = async () => {
        if (!service.id) return;
        if (!seedPrice || seedPrice <= 0) {
            notify.error('error', 'seed_price');
            return;
        }
        setIsApplyingCosts(true);
        try {
            const nextHistory = buildUpdatedHistory(seedPrice);
            setSeedPricesHistory(nextHistory);

            const configToSave: PriceGeneratorConfig = {
                ...currentConfig,
                seed_price: seedPrice,
                seed_prices: nextHistory,
            };

            await updateServiceMutation.mutateAsync({
                id: service.id,
                price_generator_config: configToSave
            });

            if (providersList.length > 0) {
                for (const provider of providersList) {
                    const key = provider.id ? provider.id.toString() : 'all';
                    const costCalc = calculatedCosts[key];
                    if (costCalc) {
                        const costPayload: Partial<ServiceSellCost> = {
                            service_id: service.id,
                            business_id: provider.id || null,
                            start_day: startDay,
                            end_day: endDay,
                            default_amount: costCalc.default.rounded,
                            default_currency: categories.default.currency,
                            foreigner_amount: costCalc.foreigner ? costCalc.foreigner.rounded : null,
                            foreigner_currency: costCalc.foreigner ? categories.foreigner.currency : null,
                            child_amount: costCalc.child ? costCalc.child.rounded : null,
                            child_currency: costCalc.child ? categories.child.currency : null,
                            senior_amount: costCalc.senior ? costCalc.senior.rounded : null,
                            senior_currency: costCalc.senior ? categories.senior.currency : null,
                            infant_amount: costCalc.infant ? costCalc.infant.rounded : null,
                            infant_currency: costCalc.infant ? categories.infant.currency : null,
                        };
                        await createCostMutation.mutateAsync(costPayload);

                        const rawProviderMult = providerMultipliers[key];
                        if (rawProviderMult !== undefined && provider.id) {
                            const providerMult = rawProviderMult === '' || rawProviderMult == null ? null : Number(rawProviderMult);
                            const existingMult = provider.tenant_business?.cost_multiplier != null ? Number(provider.tenant_business.cost_multiplier) : null;
                            if (providerMult !== existingMult) {
                                await updateTenantBusinessMutation.mutateAsync({
                                    ...(provider.tenant_business?.id ? { id: provider.tenant_business.id } : { business_id: provider.id }),
                                    cost_multiplier: providerMult
                                });
                            }
                        }
                    }
                }
            } else {
                const costCalc = calculatedCosts['all'];
                if (costCalc) {
                    const costPayload: Partial<ServiceSellCost> = {
                        service_id: service.id,
                        business_id: null,
                        start_day: startDay,
                        end_day: endDay,
                        default_amount: costCalc.default.rounded,
                        default_currency: categories.default.currency,
                        foreigner_amount: costCalc.foreigner ? costCalc.foreigner.rounded : null,
                        foreigner_currency: costCalc.foreigner ? categories.foreigner.currency : null,
                        child_amount: costCalc.child ? costCalc.child.rounded : null,
                        child_currency: costCalc.child ? categories.child.currency : null,
                        senior_amount: costCalc.senior ? costCalc.senior.rounded : null,
                        senior_currency: costCalc.senior ? categories.senior.currency : null,
                        infant_amount: costCalc.infant ? costCalc.infant.rounded : null,
                        infant_currency: costCalc.infant ? categories.infant.currency : null,
                    };
                    await createCostMutation.mutateAsync(costPayload);
                }
            }

            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceSellCosts.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.businesses.all });

            notify.success('success', 'costs_applied_successfully');
        } catch (err: any) {
            notify.error('error', err?.message || 'operation.error');
        } finally {
            setIsApplyingCosts(false);
        }
    };

    return (
        <div className="space-y-4">
            {/* Top Parameters Bar */}
            <div className="space-y-4 solid-box-border-tinted">
                <div className="flex flex-wrap gap-2">
                    <CurrencySelector
                        size="sm"
                        label={t('currency')}
                        value={currency}
                        onChange={(c) => handleTopCurrencyChange(c || DEFAULT_CURRENCY)}
                        disabled={false}
                        className="w-fit"
                    />
                    <MoneyNumberInput
                        size="sm"
                        label={t('seed_price')}
                        amount={seedPrice}
                        onChange={(val) => setSeedPrice(val != null ? val : null)}
                        className="font-bold"
                    />
                    <DatePickerCustom
                        label={t('start_day')}
                        size="sm"
                        value={startDay || ''}
                        onChange={(val) => setStartDay(val?.[0] || null)}
                    />
                    <DatePickerCustom
                        label={t('end_day')}
                        size="sm"
                        value={endDay || ''}
                        onChange={(val) => setEndDay(val?.[0] || null)}
                    />
                </div>
                <NumberInput
                    size="sm"
                    label={t('tax_modifier')}
                    value={taxModifier}
                    onChange={(val) => setTaxModifier(val)}
                    hideControls
                    className="w-fit"
                />
                <div className="flex flex-wrap gap-2">
                    <Select
                        size="sm"
                        label={t('round_to')}
                        value={String(roundTo)}
                        onChange={(val) => setRoundTo(val ? Number(val) : 1)}
                        data={[
                            { value: '0.01', label: '0.01' },
                            { value: '0.1', label: '0.1' },
                            { value: '1', label: '1' },
                            { value: '10', label: '10' },
                            { value: '50', label: '50' },
                            { value: '100', label: '100' },
                            { value: '500', label: '500' },
                            { value: '1000', label: '1,000' },
                            { value: '5000', label: '5,000' },
                            { value: '10000', label: '10,000' },
                        ]}
                    />
                    <Select
                        size="sm"
                        label={t('round_method')}
                        value={roundMethod}
                        onChange={(val: any) => setRoundMethod(val || 'round')}
                        data={[
                            { value: 'round', label: t('round_nearest') },
                            { value: 'ceil', label: t('round_ceil') },
                            { value: 'floor', label: t('round_floor') },
                        ]}
                    />
                </div>
            </div>

            {/* Category Price Differentials Table */}
            <div>
                <div className="subtle-table">
                    <table>
                        <thead>
                            <tr>
                                <th style={{ width: 40, textAlign: 'center' }}></th>
                                <th>{t('pax_category')}</th>
                                <th style={{ width: 140 }}>{t('currency')}</th>
                                <th style={{ width: 140 }}>{t('differential')}</th>
                                <th style={{ width: 90, textAlign: 'center' }}>Offset</th>
                                <th className="number" style={{ width: 140 }}>{t('raw_calculation')}</th>
                                <th className="number" style={{ width: 140 }}>{t('rounded_calculation')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* 1. General */}
                            <tr>
                                <td style={{ textAlign: 'center' }}>
                                    <IconCheck size={16} className="text-emerald-600 inline-block" />
                                </td>
                                <td className="font-semibold text-gray-900">
                                    {t('diff_default')}
                                </td>
                                <td>
                                    <CurrencySelector
                                        size="xs"
                                        className="w-36"
                                        value={categories.default.currency}
                                        onChange={(c) => updateCategoryCurrency('default', c || DEFAULT_CURRENCY)}
                                    />
                                </td>
                                <td>
                                    <NumberInput
                                        size="xs"
                                        hideControls
                                        value={categories.default.diff}
                                        onChange={(v) => updateCategoryDiff('default', v)}
                                    />
                                </td>
                                <td style={{ textAlign: 'center' }} className="font-mono">
                                    <Badge size="xs" color="gray" variant="light">
                                        {modifierToPercent(categories.default.diff)}
                                    </Badge>
                                </td>
                                <td className="number font-mono text-gray-500">
                                    {formatPrice(calculatedSellPrices.default.raw, { currency: categories.default.currency })}
                                </td>
                                <td className="number font-mono font-bold text-gray-900">
                                    {formatPrice(calculatedSellPrices.default.rounded, { currency: categories.default.currency })}
                                </td>
                            </tr>

                            {/* 2. Foreigner */}
                            <tr className={!categories.foreigner.enabled ? 'opacity-40' : ''}>
                                <td style={{ textAlign: 'center' }}>
                                    <Checkbox
                                        color="black"
                                        checked={categories.foreigner.enabled}
                                        onChange={(e) => toggleCategoryEnabled('foreigner', e.currentTarget.checked)}
                                    />
                                </td>
                                <td className="font-semibold text-gray-900">
                                    {t('diff_foreigner')}
                                </td>
                                <td>
                                    <CurrencySelector
                                        size="xs"
                                        className="w-36"
                                        value={categories.foreigner.currency}
                                        onChange={(c) => updateCategoryCurrency('foreigner', c || DEFAULT_CURRENCY)}
                                        disabled={!categories.foreigner.enabled}
                                    />
                                </td>
                                <td>
                                    <NumberInput
                                        size="xs"
                                        hideControls
                                        disabled={!categories.foreigner.enabled}
                                        value={categories.foreigner.diff}
                                        onChange={(v) => updateCategoryDiff('foreigner', v)}
                                    />
                                </td>
                                <td style={{ textAlign: 'center' }} className="font-mono">
                                    <Badge size="xs" color="blue" variant="light">
                                        {modifierToPercent(categories.foreigner.diff)}
                                    </Badge>
                                </td>
                                <td className="number font-mono text-gray-500">
                                    {calculatedSellPrices.foreigner ? formatPrice(calculatedSellPrices.foreigner.raw, { currency: categories.foreigner.currency }) : '-'}
                                </td>
                                <td className="number font-mono font-bold text-blue-700">
                                    {calculatedSellPrices.foreigner ? formatPrice(calculatedSellPrices.foreigner.rounded, { currency: categories.foreigner.currency }) : '-'}
                                </td>
                            </tr>

                            {/* 3. Child */}
                            <tr className={!categories.child.enabled ? 'opacity-40' : ''}>
                                <td style={{ textAlign: 'center' }}>
                                    <Checkbox
                                        color="black"
                                        checked={categories.child.enabled}
                                        onChange={(e) => toggleCategoryEnabled('child', e.currentTarget.checked)}
                                    />
                                </td>
                                <td className="font-semibold text-gray-900">
                                    {t('diff_child')}
                                </td>
                                <td>
                                    <CurrencySelector
                                        size="xs"
                                        className="w-36"
                                        value={categories.child.currency}
                                        onChange={(c) => updateCategoryCurrency('child', c || DEFAULT_CURRENCY)}
                                        disabled={!categories.child.enabled}
                                    />
                                </td>
                                <td>
                                    <NumberInput
                                        size="xs"
                                        hideControls
                                        disabled={!categories.child.enabled}
                                        value={categories.child.diff}
                                        onChange={(v) => updateCategoryDiff('child', v)}
                                    />
                                </td>
                                <td style={{ textAlign: 'center' }} className="font-mono">
                                    <Badge size="xs" color="teal" variant="light">
                                        {modifierToPercent(categories.child.diff)}
                                    </Badge>
                                </td>
                                <td className="number font-mono text-gray-500">
                                    {calculatedSellPrices.child ? formatPrice(calculatedSellPrices.child.raw, { currency: categories.child.currency }) : '-'}
                                </td>
                                <td className="number font-mono font-bold text-teal-700">
                                    {calculatedSellPrices.child ? formatPrice(calculatedSellPrices.child.rounded, { currency: categories.child.currency }) : '-'}
                                </td>
                            </tr>

                            {/* 4. Senior */}
                            <tr className={!categories.senior.enabled ? 'opacity-40' : ''}>
                                <td style={{ textAlign: 'center' }}>
                                    <Checkbox
                                        color="black"
                                        checked={categories.senior.enabled}
                                        onChange={(e) => toggleCategoryEnabled('senior', e.currentTarget.checked)}
                                    />
                                </td>
                                <td className="font-semibold text-gray-900">
                                    <div className="flex items-center justify-between gap-2">
                                        <span>{service.show_retired ? t('diff_retired') : t('diff_senior')}</span>
                                        <Switch
                                            size="xs"
                                            color="black"
                                            checked={Boolean(service.show_retired)}
                                            onChange={(e) => {
                                                updateServiceMutation.mutate({
                                                    id: service.id,
                                                    show_retired: e.currentTarget.checked
                                                });
                                            }}
                                            label={t('show_retired')}
                                        />
                                    </div>
                                </td>
                                <td>
                                    <CurrencySelector
                                        size="xs"
                                        className="w-36"
                                        value={categories.senior.currency}
                                        onChange={(c) => updateCategoryCurrency('senior', c || DEFAULT_CURRENCY)}
                                        disabled={!categories.senior.enabled}
                                    />
                                </td>
                                <td>
                                    <NumberInput
                                        size="xs"
                                        hideControls
                                        disabled={!categories.senior.enabled}
                                        value={categories.senior.diff}
                                        onChange={(v) => updateCategoryDiff('senior', v)}
                                    />
                                </td>
                                <td style={{ textAlign: 'center' }} className="font-mono">
                                    <Badge size="xs" color="indigo" variant="light">
                                        {modifierToPercent(categories.senior.diff)}
                                    </Badge>
                                </td>
                                <td className="number font-mono text-gray-500">
                                    {calculatedSellPrices.senior ? formatPrice(calculatedSellPrices.senior.raw, { currency: categories.senior.currency }) : '-'}
                                </td>
                                <td className="number font-mono font-bold text-indigo-700">
                                    {calculatedSellPrices.senior ? formatPrice(calculatedSellPrices.senior.rounded, { currency: categories.senior.currency }) : '-'}
                                </td>
                            </tr>

                            {/* 5. Infant */}
                            <tr className={!categories.infant.enabled ? 'opacity-40' : ''}>
                                <td style={{ textAlign: 'center' }}>
                                    <Checkbox
                                        color="black"
                                        checked={categories.infant.enabled}
                                        onChange={(e) => toggleCategoryEnabled('infant', e.currentTarget.checked)}
                                    />
                                </td>
                                <td className="font-semibold text-gray-900">
                                    {t('diff_infant')}
                                </td>
                                <td>
                                    <CurrencySelector
                                        size="xs"
                                        className="w-36"
                                        value={categories.infant.currency}
                                        onChange={(c) => updateCategoryCurrency('infant', c || DEFAULT_CURRENCY)}
                                        disabled={!categories.infant.enabled}
                                    />
                                </td>
                                <td>
                                    <NumberInput
                                        size="xs"
                                        hideControls
                                        disabled={!categories.infant.enabled}
                                        value={categories.infant.diff}
                                        onChange={(v) => updateCategoryDiff('infant', v)}
                                    />
                                </td>
                                <td style={{ textAlign: 'center' }} className="font-mono">
                                    <Badge size="xs" color="gray" variant="light">
                                        {modifierToPercent(categories.infant.diff)}
                                    </Badge>
                                </td>
                                <td className="number font-mono text-gray-500">
                                    {calculatedSellPrices.infant ? formatPrice(calculatedSellPrices.infant.raw, { currency: categories.infant.currency }) : '-'}
                                </td>
                                <td className="number font-mono font-bold text-gray-700">
                                    {calculatedSellPrices.infant ? formatPrice(calculatedSellPrices.infant.rounded, { currency: categories.infant.currency }) : '-'}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="flex justify-end pt-2">
                    <Button
                        color="black"
                        size="xs"
                        radius="md"
                        leftSection={<IconSparkles size={14} />}
                        onClick={handleApplyPrices}
                        loading={isApplyingPrices}
                    >
                        {t('apply_to_prices')}
                    </Button>
                </div>
            </div>

            {/* Provider Costs Section */}
            <div className="pt-2">
                <div className="flex flex-wrap items-center justify-between gap-2 pb-2">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-gray-900">
                            {t('preview_provider_costs')}
                        </span>
                        <Badge color="gray" variant="light" size="xs">
                            {providersList.length} {t('providers')}
                        </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-500">
                            {t('calculate_costs_from')}:
                        </span>
                        <SegmentedControl
                            size="xs"
                            value={costBase}
                            onChange={(val) => setCostBase(val as 'seed_price' | 'sell_prices')}
                            data={[
                                { label: t('seed_price'), value: 'seed_price' },
                                { label: t('sell_prices'), value: 'sell_prices' },
                            ]}
                        />
                    </div>
                </div>

                <div className="subtle-table">
                    <table>
                        <thead>
                            <tr>
                                <th>{t('providers')}</th>
                                <th style={{ width: 180 }}>{t('cost_multiplier')}</th>
                                <th className="number">{t('diff_default')}</th>
                                {categories.foreigner.enabled && <th className="number">{t('diff_foreigner')}</th>}
                                {categories.child.enabled && <th className="number">{t('diff_child')}</th>}
                                {categories.senior.enabled && <th className="number">{service.show_retired ? t('diff_retired') : t('diff_senior')}</th>}
                                {categories.infant.enabled && <th className="number">{t('diff_infant')}</th>}
                                <th className="number">Margin</th>
                            </tr>
                        </thead>
                        <tbody>
                            {providersList.length === 0 ? (
                                <tr>
                                    <td>
                                        <div className="flex items-center gap-1.5 text-gray-600">
                                            <IconBuildingStore size={15} className="text-gray-400 shrink-0" />
                                            <span className="font-medium text-gray-700">{t('all_providers')}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <NumberInput
                                            size="xs"
                                            hideControls
                                            value={providerMultipliers['all'] ?? ''}
                                            onChange={(v) => setProviderMultipliers(prev => ({ ...prev, all: v }))}
                                        />
                                    </td>
                                    <td className="number font-mono font-bold text-emerald-800">
                                        {formatPrice(calculatedCosts['all']?.default.rounded || 0, { currency: categories.default.currency })}
                                    </td>
                                    {categories.foreigner.enabled && (
                                        <td className="number font-mono text-emerald-800">
                                            {calculatedCosts['all']?.foreigner ? formatPrice(calculatedCosts['all'].foreigner.rounded, { currency: categories.foreigner.currency }) : '-'}
                                        </td>
                                    )}
                                    {categories.child.enabled && (
                                        <td className="number font-mono text-emerald-800">
                                            {calculatedCosts['all']?.child ? formatPrice(calculatedCosts['all'].child.rounded, { currency: categories.child.currency }) : '-'}
                                        </td>
                                    )}
                                    {categories.senior.enabled && (
                                        <td className="number font-mono text-emerald-800">
                                            {calculatedCosts['all']?.senior ? formatPrice(calculatedCosts['all'].senior.rounded, { currency: categories.senior.currency }) : '-'}
                                        </td>
                                    )}
                                    {categories.infant.enabled && (
                                        <td className="number font-mono text-emerald-800">
                                            {calculatedCosts['all']?.infant ? formatPrice(calculatedCosts['all'].infant.rounded, { currency: categories.infant.currency }) : '-'}
                                        </td>
                                    )}
                                    <td className="number font-mono font-bold text-emerald-900">
                                        <span className="inline-flex items-center gap-0.5">
                                            <IconTrendingUp size={12} className="text-emerald-600" />
                                            +{(((calculatedSellPrices.default.rounded - (calculatedCosts['all']?.default.rounded || 0)) / (calculatedSellPrices.default.rounded || 1)) * 100).toFixed(1)}%
                                        </span>
                                    </td>
                                </tr>
                            ) : (
                                providersList.map(provider => {
                                    const key = provider.id ? provider.id.toString() : 'all';
                                    const currentMultiplier = key in providerMultipliers
                                        ? providerMultipliers[key]
                                        : (provider.tenant_business?.cost_multiplier != null && Number(provider.tenant_business.cost_multiplier) !== 0
                                            ? Number(provider.tenant_business.cost_multiplier)
                                            : '');
                                    const costData = calculatedCosts[key];

                                    const defaultSell = calculatedSellPrices.default.rounded;
                                    const defaultCost = costData?.default.rounded || 0;
                                    const marginAmount = defaultSell - defaultCost;
                                    const marginPercent = defaultSell > 0 ? (marginAmount / defaultSell) * 100 : 0;

                                    return (
                                        <tr key={key}>
                                            <td>
                                                <div className="flex items-center gap-1.5">
                                                    <IconBuildingStore size={15} className="text-gray-400 shrink-0" />
                                                    <span className="font-bold text-gray-900">{provider.name}</span>
                                                    {provider.category && <TagIcon name={provider.category} size="xxs" hideText />}
                                                    {provider.city && <span className="text-xs text-gray-400">({provider.city})</span>}
                                                </div>
                                            </td>
                                            <td>
                                                <NumberInput
                                                    size="xs"
                                                    hideControls
                                                    value={currentMultiplier}
                                                    onChange={val => setProviderMultipliers(prev => ({ ...prev, [key]: val }))}
                                                />
                                            </td>
                                            <td className="number font-mono font-bold text-emerald-800">
                                                {formatPrice(costData?.default.rounded || 0, { currency: categories.default.currency })}
                                            </td>
                                            {categories.foreigner.enabled && (
                                                <td className="number font-mono text-emerald-800">
                                                    {costData?.foreigner ? formatPrice(costData.foreigner.rounded, { currency: categories.foreigner.currency }) : '-'}
                                                </td>
                                            )}
                                            {categories.child.enabled && (
                                                <td className="number font-mono text-emerald-800">
                                                    {costData?.child ? formatPrice(costData.child.rounded, { currency: categories.child.currency }) : '-'}
                                                </td>
                                            )}
                                            {categories.senior.enabled && (
                                                <td className="number font-mono text-emerald-800">
                                                    {costData?.senior ? formatPrice(costData.senior.rounded, { currency: categories.senior.currency }) : '-'}
                                                </td>
                                            )}
                                            {categories.infant.enabled && (
                                                <td className="number font-mono text-emerald-800">
                                                    {costData?.infant ? formatPrice(costData.infant.rounded, { currency: categories.infant.currency }) : '-'}
                                                </td>
                                            )}
                                            <td className="number font-mono font-bold text-emerald-900">
                                                <span className="inline-flex items-center gap-0.5">
                                                    <IconTrendingUp size={12} className="text-emerald-600" />
                                                    +{marginPercent.toFixed(1)}%
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex justify-end pt-2">
                    <Button
                        color="black"
                        size="xs"
                        radius="md"
                        leftSection={<IconSparkles size={14} />}
                        onClick={handleApplyCosts}
                        loading={isApplyingCosts}
                    >
                        {t('apply_to_costs')}
                    </Button>
                </div>
            </div>

            {/* Seed Prices History Section */}
            <div className="pt-2">
                <div className="flex items-center justify-between pb-2">
                    <span className="text-sm font-bold text-gray-900">
                        {t('seed_prices_history')}
                    </span>
                    <Badge color="gray" variant="light" size="xs">
                        {seedPricesHistory.length}
                    </Badge>
                </div>

                <div className="subtle-table">
                    <table>
                        <thead>
                            <tr>
                                <th className="number">{t('amount')}</th>
                                <th>{t('currency')}</th>
                                <th>{t('dates_used')}</th>
                                <th style={{ width: 140 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {seedPricesHistory.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="text-center text-gray-400 py-3">
                                        {t('no_seed_prices')}
                                    </td>
                                </tr>
                            ) : (
                                seedPricesHistory.map((item) => (
                                    <tr key={item.id}>
                                        <td className="number font-mono font-bold text-gray-900">
                                            {formatPrice(item.amount, { currency: item.currency })}
                                        </td>
                                        <td>
                                            <span className="font-semibold text-xs text-gray-700">{item.currency}</span>
                                        </td>
                                        <td className="text-xs text-gray-600">
                                            {item.start_day ? formatDay(item.start_day) : '—'} – {item.end_day ? formatDay(item.end_day) : '—'}
                                        </td>
                                        <td className="text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button
                                                    size="xs"
                                                    variant="subtle"
                                                    color="dark"
                                                    onClick={() => {
                                                        setSeedPrice(item.amount);
                                                        setCurrency(item.currency);
                                                        setStartDay(item.start_day || null);
                                                        setEndDay(item.end_day || null);
                                                    }}
                                                >
                                                    {t('use_this_seed_price')}
                                                </Button>
                                                <Button
                                                    size="xs"
                                                    variant="subtle"
                                                    color="red"
                                                    p={4}
                                                    onClick={async () => {
                                                        const nextHistory = seedPricesHistory.filter(h => h.id !== item.id);
                                                        setSeedPricesHistory(nextHistory);
                                                        if (service.id) {
                                                            try {
                                                                await updateServiceMutation.mutateAsync({
                                                                    id: service.id,
                                                                    price_generator_config: {
                                                                        ...currentConfig,
                                                                        seed_prices: nextHistory
                                                                    }
                                                                });
                                                                queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
                                                            } catch (err: any) {
                                                                notify.error('error', err?.message || 'operation.error');
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <IconTrash size={14} />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
